import * as React from "react";
import { FieldValues, Path, useFormContext } from "react-hook-form";
import { useLanguage } from "../../Language";
import { isFieldRequiredByNotification } from "./AdditionalEmailGroup";
import { BorrowerNotificationType } from "@tlc/ls2pac/dto/BorrowerNotificationType";
import { EmailField } from "./EmailField";
import { useWatchWithCallback } from "@tlc/forms/FormTextField";

export interface PrimaryEmailProps<T extends FieldValues> {
  name: Path<T>;
  required?: boolean;
  hidden?: boolean;
  disabled?: boolean;
  confirmEmail?: Path<T>;
  email2: Path<T>;
  email3: Path<T>;
  notificationTypes: BorrowerNotificationType[][];
}

export const PrimaryEmail = <T extends FieldValues>({
  name,
  required = false,
  hidden = false,
  disabled = false,
  confirmEmail,
  email2,
  email3,
  notificationTypes,
}: PrimaryEmailProps<T>) => {
  const { translate } = useLanguage();
  const { getValues, trigger, getFieldState } = useFormContext<T>();

  // using boolean state variables will trigger fewer re-renders than tracking the value
  const [emailFilled, setEmailFilled] = React.useState<boolean>(
    !!getValues(name),
  );
  const [email2Filled, setEmail2Filled] = React.useState<boolean>(
    !!getValues(email2),
  );
  const [email3Filled, setEmail3Filled] = React.useState<boolean>(
    !!getValues(email3),
  );

  // TODO does getFieldState trigger top level re-render?
  const { isTouched: emailTouched } = getFieldState(name);
  const { isTouched: confirmEmailTouched } = getFieldState(confirmEmail);

  useWatchWithCallback(name, (value) => {
    setEmailFilled(!!value);
  });

  useWatchWithCallback(email2, (value) => {
    setEmail2Filled(!!value);
  });

  useWatchWithCallback(email3, (value) => {
    setEmail3Filled(!!value);
  });

  const isEmailRequiredByNotifications = isFieldRequiredByNotification(
    notificationTypes,
    BorrowerNotificationType.Email,
  );

  const emailRequired = React.useMemo(() => {
    const message = required
      ? translate("isRequired", {
          LABEL: translate("ci_email"),
        })
      : isEmailRequiredByNotifications
      ? translate("isRequiredWhenNoticeTypeIs", {
          LABEL: translate("ci_email"),
          NOTICE_TYPE: translate("ci_notificationType_Email"),
        })
      : email2Filled
      ? translate("emailRequiredWhenEmail2Present")
      : email3Filled
      ? translate("emailRequiredWhenEmail3Present")
      : null;
    return message;
  }, [email2Filled, email3Filled, isEmailRequiredByNotifications, required]);

  React.useEffect(() => {
    if (emailTouched) {
      trigger(name);
    }
    if (confirmEmailTouched) {
      trigger(confirmEmail);
    }
  }, [emailRequired]);

  return (
    (!hidden || emailRequired) && (
      <>
        <EmailField<T>
          disabled={disabled}
          sx={{ maxWidth: { sm: "320px", md: "648px" } }}
          name={name}
          label={translate("ci_email")}
          options={{ required: emailRequired }}
        ></EmailField>
        {confirmEmail && (
          <EmailField<T>
            disabled={disabled}
            name={confirmEmail}
            label={translate("ci_confirmEmail")}
            sx={{ maxWidth: { sm: "320px", md: "648px" } }}
            required={!!emailRequired || emailFilled}
            options={{
              validate: (value) => {
                if (value === getValues(name)) {
                  return true;
                }
                return translate("confirmEmailMustMatchError");
              },
            }}
          />
        )}
      </>
    )
  );
};
